import { useState, Fragment } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, Transition, Listbox, Tab } from '@headlessui/react';
import {
  PlusIcon,
  ChevronUpDownIcon,
  CheckIcon,
  ShieldExclamationIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ScaleIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { enforcementIntelligenceApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import FeatureGate from '../components/FeatureGate';
import { ModuleIntro } from '../components/ui';
import clsx from 'clsx';

const ACTION_TYPES = [
  { value: 'CONSENT_ORDER', label: 'Consent Order' },
  { value: 'CEASE_AND_DESIST', label: 'Cease and Desist' },
  { value: 'CIVIL_MONEY_PENALTY', label: 'Civil Money Penalty' },
  { value: 'SETTLEMENT', label: 'Settlement' },
  { value: 'FORMAL_AGREEMENT', label: 'Formal Agreement' },
  { value: 'REFERRAL', label: 'Referral' },
  { value: 'OTHER_ACTION', label: 'Other Action' },
];

const ACTION_TYPE_BADGE: Record<string, { label: string; className: string }> = {
  CONSENT_ORDER: { label: 'Consent Order', className: 'bg-red-100 text-red-700' },
  CEASE_AND_DESIST: { label: 'Cease and Desist', className: 'bg-orange-100 text-orange-700' },
  CIVIL_MONEY_PENALTY: { label: 'Civil Money Penalty', className: 'bg-yellow-100 text-yellow-700' },
  SETTLEMENT: { label: 'Settlement', className: 'bg-blue-100 text-blue-700' },
  FORMAL_AGREEMENT: { label: 'Formal Agreement', className: 'bg-purple-100 text-purple-700' },
  REFERRAL: { label: 'Referral', className: 'bg-indigo-100 text-indigo-700' },
  OTHER_ACTION: { label: 'Other Action', className: 'bg-gray-100 text-gray-700' },
};

const EXPOSURE_BADGE: Record<string, { label: string; className: string }> = {
  HIGH: { label: 'High', className: 'bg-red-100 text-red-700' },
  MEDIUM: { label: 'Medium', className: 'bg-yellow-100 text-yellow-700' },
  LOW: { label: 'Low', className: 'bg-green-100 text-green-700' },
};

const TYPE_FILTERS = [
  { value: '', label: 'All Types' },
  ...ACTION_TYPES,
];

const EMPTY_INGEST_FORM = {
  institutionName: '',
  actionType: 'CONSENT_ORDER',
  agency: '',
  actionDate: '',
  description: '',
  penaltyAmount: '',
  violationTypes: '',
  complianceAreas: '',
  sourceUrl: '',
  state: '',
  institutionType: '',
  assetSize: '',
};

function formatPenalty(amount: number | null | undefined): string {
  // Most enforcement actions are consent orders/settlements without a stated
  // monetary penalty — show that explicitly so users don't read `--` as a
  // data-quality bug. The Total Penalties stat (sum of non-null amounts)
  // confirms data IS being captured for the actions that have it.
  if (!amount && amount !== 0) return 'Not stated';
  if (amount >= 1000000) {
    return '$' + (amount / 1000000).toFixed(1) + 'M';
  }
  if (amount >= 1000) {
    return '$' + (amount / 1000).toFixed(0) + 'K';
  }
  return '$' + amount.toLocaleString();
}

export default function EnforcementIntelligencePage() {
  const queryClient = useQueryClient();
  const [typeFilter, setTypeFilter] = useState('');
  const [agencySearch, setAgencySearch] = useState('');
  const [showIngest, setShowIngest] = useState(false);
  const [viewId, setViewId] = useState<string | null>(null);
  const [analyzeId, setAnalyzeId] = useState<string | null>(null);
  const [ingestForm, setIngestForm] = useState(EMPTY_INGEST_FORM);

  // Team
  const { teamId } = useTeamId();

  // List enforcement actions
  const { data: listData, isLoading } = useQuery({
    queryKey: ['enforcementActions', teamId, typeFilter, agencySearch],
    queryFn: () =>
      enforcementIntelligenceApi.list(teamId!, {
        actionType: typeFilter || undefined,
        agency: agencySearch || undefined,
      }),
    enabled: !!teamId,
  });
  const actions = listData?.data?.actions || [];
  const total = listData?.data?.total || 0;

  // Dashboard
  const { data: dashData } = useQuery({
    queryKey: ['enforcementDashboard', teamId],
    queryFn: () => enforcementIntelligenceApi.getDashboard(teamId!),
    enabled: !!teamId,
  });
  const dashboard = dashData?.data;

  // Trends
  const { data: trendsData } = useQuery({
    queryKey: ['enforcementTrends', teamId],
    queryFn: () => enforcementIntelligenceApi.getTrends(teamId!, { months: 24 }),
    enabled: !!teamId,
  });
  const trends = trendsData?.data?.trends || [];

  // View single action
  const { data: viewData } = useQuery({
    queryKey: ['enforcementAction', teamId, viewId],
    queryFn: () => enforcementIntelligenceApi.get(teamId!, viewId!),
    enabled: !!teamId && !!viewId,
  });
  const viewAction = viewData?.data;

  // Ingest mutation
  const ingestMutation = useMutation({
    mutationFn: (data: any) => enforcementIntelligenceApi.ingest(teamId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enforcementActions'] });
      queryClient.invalidateQueries({ queryKey: ['enforcementDashboard'] });
      toast.success('Enforcement action ingested successfully');
      setShowIngest(false);
      setIngestForm(EMPTY_INGEST_FORM);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to ingest enforcement action');
    },
  });

  // Analyze mutation
  const analyzeMutation = useMutation({
    mutationFn: (id: string) => enforcementIntelligenceApi.analyze(teamId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enforcementActions'] });
      queryClient.invalidateQueries({ queryKey: ['enforcementDashboard'] });
      queryClient.invalidateQueries({ queryKey: ['enforcementAction'] });
      toast.success('Exposure analysis complete');
      setAnalyzeId(null);
      // If we are viewing the action, refresh view data
      if (viewId) {
        queryClient.invalidateQueries({ queryKey: ['enforcementAction', teamId, viewId] });
      }
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to analyze exposure');
      setAnalyzeId(null);
    },
  });

  const handleIngest = () => {
    ingestMutation.mutate({
      institutionName: ingestForm.institutionName,
      actionType: ingestForm.actionType,
      agency: ingestForm.agency,
      actionDate: ingestForm.actionDate || undefined,
      description: ingestForm.description || undefined,
      penaltyAmount: ingestForm.penaltyAmount
        ? parseFloat(ingestForm.penaltyAmount)
        : undefined,
      violationTypes: ingestForm.violationTypes
        ? ingestForm.violationTypes.split(',').map((s) => s.trim()).filter(Boolean)
        : undefined,
      complianceAreas: ingestForm.complianceAreas
        ? ingestForm.complianceAreas.split(',').map((s) => s.trim()).filter(Boolean)
        : undefined,
      sourceUrl: ingestForm.sourceUrl || undefined,
      state: ingestForm.state || undefined,
      institutionType: ingestForm.institutionType || undefined,
      assetSize: ingestForm.assetSize ? parseFloat(ingestForm.assetSize) : undefined,
    });
  };

  const handleAnalyze = (id: string) => {
    setAnalyzeId(id);
    analyzeMutation.mutate(id);
  };

  // Summary calculations
  const totalPenalties = actions.reduce(
    (sum: number, a: any) => sum + (a.penaltyAmount || 0),
    0
  );
  const highExposureCount = actions.filter(
    (a: any) => a.exposureLevel === 'HIGH'
  ).length;

  const currentYear = new Date().getFullYear();
  const thisYearCount = actions.filter((a: any) => {
    if (!a.actionDate) return false;
    return new Date(a.actionDate).getFullYear() === currentYear;
  }).length;
  const lastYearCount = actions.filter((a: any) => {
    if (!a.actionDate) return false;
    return new Date(a.actionDate).getFullYear() === currentYear - 1;
  }).length;

  return (
    <FeatureGate feature="enforcement_intelligence" mode="lock">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Enforcement Intelligence</h1>
            <p className="mt-1 text-sm text-gray-500">
              Track enforcement actions, analyze exposure, and monitor regulatory trends
            </p>
          </div>
          <button
            onClick={() => setShowIngest(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700"
          >
            <PlusIcon className="h-4 w-4" />
            Ingest Action
          </button>
        </div>

        {/* First-time orientation — what this module is, where its data comes from, what Analyze does. */}
        <ModuleIntro storageKey="enforcement-intelligence" title="What is Enforcement Intelligence?">
          <p>
            Clarid automatically tracks public enforcement actions issued by federal regulators, so you can
            watch what supervisors are penalizing across the industry and gauge how exposed your own
            institution may be to the same issues.
          </p>
          <ModuleIntro.Points
            items={[
              <>
                <span className="font-medium text-gray-900">Where the actions come from:</span> Clarid pulls
                them automatically from the public CFPB, OCC, and FDIC newsroom and press-release feeds
                (duplicates are removed). You can also add one yourself with <span className="font-medium">Ingest Action</span>.
              </>,
              <>
                <span className="font-medium text-gray-900">This is industry monitoring, not a peer comparison.</span>{' '}
                It shows what regulators are doing across the market — it does not rank your bank against a peer group.
              </>,
              <>
                <span className="font-medium text-gray-900">What “Analyze” / “Run Exposure Analysis” does:</span>{' '}
                it checks each action against your own compliance footprint in Clarid — whether you have HMDA data
                (fair-lending overlap), exception tracking (pricing/underwriting), content screening (UDAAP/advertising),
                and the penalty size — then scores how relevant the action is to you (Low / Medium / High) and suggests
                next steps. Your data never leaves Clarid; the score just reflects which modules you use.
              </>,
            ]}
          />
        </ModuleIntro>

        {/* Summary cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-blue-50 p-2">
                <ScaleIcon className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Actions</p>
                <p className="text-2xl font-bold text-gray-900">{total}</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-green-50 p-2">
                <CurrencyDollarIcon className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Penalties</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatPenalty(totalPenalties)}
                </p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-red-50 p-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">High Exposure</p>
                <p className="text-2xl font-bold text-red-600">{highExposureCount}</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-indigo-50 p-2">
                <BoltIcon className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">This Year vs Last Year</p>
                <p className="text-2xl font-bold text-gray-900">
                  {thisYearCount}{' '}
                  <span className="text-sm font-normal text-gray-500">
                    vs {lastYearCount}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tab.Group>
          <Tab.List className="flex gap-4 border-b">
            <Tab
              className={({ selected }) =>
                clsx(
                  'border-b-2 px-1 py-2 text-sm font-medium',
                  selected
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                )
              }
            >
              Actions
            </Tab>
            <Tab
              className={({ selected }) =>
                clsx(
                  'border-b-2 px-1 py-2 text-sm font-medium',
                  selected
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                )
              }
            >
              Dashboard
            </Tab>
            <Tab
              className={({ selected }) =>
                clsx(
                  'border-b-2 px-1 py-2 text-sm font-medium',
                  selected
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                )
              }
            >
              Trends
            </Tab>
          </Tab.List>

          <Tab.Panels>
            {/* Actions List Tab */}
            <Tab.Panel className="space-y-4 pt-4">
              {/* Filters */}
              <div className="flex flex-wrap items-center gap-3">
                <Listbox value={typeFilter} onChange={setTypeFilter}>
                  <div className="relative w-52">
                    <Listbox.Button className="relative w-full cursor-pointer rounded-lg border bg-white py-2 pl-3 pr-10 text-left text-sm">
                      <span>
                        {TYPE_FILTERS.find((f) => f.value === typeFilter)?.label}
                      </span>
                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                      </span>
                    </Listbox.Button>
                    <Transition
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
                        {TYPE_FILTERS.map((f) => (
                          <Listbox.Option
                            key={f.value}
                            value={f.value}
                            className={({ active }) =>
                              clsx(
                                'cursor-pointer select-none py-2 pl-10 pr-4 text-sm',
                                active ? 'bg-blue-50 text-blue-700' : 'text-gray-900'
                              )
                            }
                          >
                            {({ selected }) => (
                              <>
                                <span
                                  className={clsx(
                                    'block truncate',
                                    selected && 'font-medium'
                                  )}
                                >
                                  {f.label}
                                </span>
                                {selected && (
                                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                    <CheckIcon className="h-4 w-4" />
                                  </span>
                                )}
                              </>
                            )}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </Transition>
                  </div>
                </Listbox>

                <input
                  type="text"
                  placeholder="Search by agency..."
                  value={agencySearch}
                  onChange={(e) => setAgencySearch(e.target.value)}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              {/* Actions table */}
              <div className="rounded-lg border bg-white">
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
                  </div>
                ) : actions.length === 0 ? (
                  <div className="py-12 text-center text-gray-500">
                    <ShieldExclamationIcon className="mx-auto h-12 w-12 text-gray-300" />
                    <p className="mt-4 text-lg font-medium">No enforcement actions found</p>
                    <p className="mt-1 text-sm">
                      Ingest your first enforcement action to get started.
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            Institution
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            Agency
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            Penalty
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            Exposure Level
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {actions.map((action: any) => {
                          const typeCfg =
                            ACTION_TYPE_BADGE[action.actionType] ||
                            ACTION_TYPE_BADGE.OTHER_ACTION;
                          const exposureCfg = action.exposureLevel
                            ? EXPOSURE_BADGE[action.exposureLevel]
                            : null;
                          return (
                            <tr key={action.id} className="hover:bg-gray-50">
                              <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                {action.actionDate
                                  ? new Date(action.actionDate).toLocaleDateString()
                                  : '--'}
                              </td>
                              <td className="max-w-xs truncate px-6 py-4 text-sm font-medium text-gray-900">
                                {action.institutionName && action.institutionName !== 'Unknown Institution'
                                  ? action.institutionName
                                  : <span className="text-gray-400 italic">Name not extracted</span>}
                              </td>
                              <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                {action.agency}
                              </td>
                              <td className="whitespace-nowrap px-6 py-4">
                                <span
                                  className={clsx(
                                    'inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium',
                                    typeCfg.className
                                  )}
                                >
                                  {typeCfg.label}
                                </span>
                              </td>
                              <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                                {formatPenalty(action.penaltyAmount)}
                              </td>
                              <td className="whitespace-nowrap px-6 py-4">
                                {exposureCfg ? (
                                  <span
                                    className={clsx(
                                      'inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium',
                                      exposureCfg.className
                                    )}
                                  >
                                    {exposureCfg.label}
                                  </span>
                                ) : (
                                  <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-500">
                                    Not Analyzed
                                  </span>
                                )}
                              </td>
                              <td className="whitespace-nowrap px-6 py-4">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => handleAnalyze(action.id)}
                                    disabled={
                                      analyzeMutation.isPending &&
                                      analyzeId === action.id
                                    }
                                    className="text-sm font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
                                  >
                                    {analyzeMutation.isPending &&
                                    analyzeId === action.id
                                      ? 'Analyzing...'
                                      : 'Analyze'}
                                  </button>
                                  <button
                                    onClick={() => setViewId(action.id)}
                                    className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
                                  >
                                    View
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </Tab.Panel>

            {/* Dashboard Tab */}
            <Tab.Panel className="space-y-6 pt-4">
              {dashboard ? (
                <>
                  {/* By Type */}
                  {dashboard.byType && dashboard.byType.length > 0 && (
                    <div className="rounded-lg border bg-white p-6">
                      <h3 className="text-lg font-semibold text-gray-900">By Type</h3>
                      <div className="mt-4 overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                                Action Type
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                                Count
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {dashboard.byType.map((item: any, idx: number) => {
                              const badge =
                                ACTION_TYPE_BADGE[item.type || item.actionType] ||
                                ACTION_TYPE_BADGE.OTHER_ACTION;
                              return (
                                <tr key={idx}>
                                  <td className="px-4 py-2">
                                    <span
                                      className={clsx(
                                        'inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium',
                                        badge.className
                                      )}
                                    >
                                      {badge.label}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-700">
                                    {item.count || item._count}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* By Agency */}
                  {dashboard.byAgency && dashboard.byAgency.length > 0 && (
                    <div className="rounded-lg border bg-white p-6">
                      <h3 className="text-lg font-semibold text-gray-900">By Agency</h3>
                      <div className="mt-4 overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                                Agency
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                                Count
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {dashboard.byAgency.map((item: any, idx: number) => (
                              <tr key={idx}>
                                <td className="px-4 py-2 text-sm text-gray-900">
                                  {item.agency || item.name}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-700">
                                  {item.count || item._count}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* By Compliance Area */}
                  {dashboard.byComplianceArea &&
                    dashboard.byComplianceArea.length > 0 && (
                      <div className="rounded-lg border bg-white p-6">
                        <h3 className="text-lg font-semibold text-gray-900">
                          By Compliance Area
                        </h3>
                        <div className="mt-4 overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                                  Area
                                </th>
                                <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                                  Count
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {dashboard.byComplianceArea.map(
                                (item: any, idx: number) => (
                                  <tr key={idx}>
                                    <td className="px-4 py-2 text-sm text-gray-900">
                                      {item.area || item.name}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-700">
                                      {item.count || item._count}
                                    </td>
                                  </tr>
                                )
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                </>
              ) : (
                <div className="flex items-center justify-center py-12">
                  <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
                </div>
              )}
            </Tab.Panel>

            {/* Trends Tab */}
            <Tab.Panel className="space-y-6 pt-4">
              {trends.length > 0 ? (
                <div className="rounded-lg border bg-white p-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Quarterly Breakdown
                  </h3>
                  <div className="mt-4 overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                            Quarter
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                            Total
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                            Penalties
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                            By Type
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {trends.map((t: any, idx: number) => (
                          <tr key={idx}>
                            <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-900">
                              {t.quarter || t.period || t.month}
                            </td>
                            <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">
                              {t.total || t.count}
                            </td>
                            <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">
                              {formatPenalty(t.totalPenalties || t.penalties)}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-700">
                              {t.byType
                                ? Object.entries(t.byType)
                                    .map(([k, v]) => {
                                      const badge = ACTION_TYPE_BADGE[k];
                                      return `${badge ? badge.label : k}: ${v}`;
                                    })
                                    .join(', ')
                                : '--'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="py-12 text-center text-gray-500">
                  <p className="text-lg font-medium">No trend data available</p>
                  <p className="mt-1 text-sm">
                    Trend data will appear once enforcement actions are tracked over time.
                  </p>
                </div>
              )}
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>

        {/* Ingest Modal */}
        <Transition appear show={showIngest} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-50"
            onClose={() => setShowIngest(false)}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      Ingest Enforcement Action
                    </Dialog.Title>
                    <div className="mt-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Institution Name
                        </label>
                        <input
                          type="text"
                          value={ingestForm.institutionName}
                          onChange={(e) =>
                            setIngestForm({
                              ...ingestForm,
                              institutionName: e.target.value,
                            })
                          }
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Action Type
                          </label>
                          <select
                            value={ingestForm.actionType}
                            onChange={(e) =>
                              setIngestForm({
                                ...ingestForm,
                                actionType: e.target.value,
                              })
                            }
                            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                          >
                            {ACTION_TYPES.map((t) => (
                              <option key={t.value} value={t.value}>
                                {t.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Agency
                          </label>
                          <input
                            type="text"
                            value={ingestForm.agency}
                            onChange={(e) =>
                              setIngestForm({
                                ...ingestForm,
                                agency: e.target.value,
                              })
                            }
                            placeholder="e.g., CFPB, OCC, FDIC"
                            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Action Date
                          </label>
                          <input
                            type="date"
                            value={ingestForm.actionDate}
                            onChange={(e) =>
                              setIngestForm({
                                ...ingestForm,
                                actionDate: e.target.value,
                              })
                            }
                            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Penalty Amount ($)
                          </label>
                          <input
                            type="number"
                            value={ingestForm.penaltyAmount}
                            onChange={(e) =>
                              setIngestForm({
                                ...ingestForm,
                                penaltyAmount: e.target.value,
                              })
                            }
                            placeholder="e.g., 1000000"
                            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Description
                        </label>
                        <textarea
                          value={ingestForm.description}
                          onChange={(e) =>
                            setIngestForm({
                              ...ingestForm,
                              description: e.target.value,
                            })
                          }
                          rows={3}
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Violation Types (comma-separated)
                        </label>
                        <input
                          type="text"
                          value={ingestForm.violationTypes}
                          onChange={(e) =>
                            setIngestForm({
                              ...ingestForm,
                              violationTypes: e.target.value,
                            })
                          }
                          placeholder="e.g., UDAAP, Fair Lending, BSA"
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Compliance Areas (comma-separated)
                        </label>
                        <input
                          type="text"
                          value={ingestForm.complianceAreas}
                          onChange={(e) =>
                            setIngestForm({
                              ...ingestForm,
                              complianceAreas: e.target.value,
                            })
                          }
                          placeholder="e.g., Fair Lending, BSA/AML, HMDA"
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Source URL
                        </label>
                        <input
                          type="text"
                          value={ingestForm.sourceUrl}
                          onChange={(e) =>
                            setIngestForm({
                              ...ingestForm,
                              sourceUrl: e.target.value,
                            })
                          }
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            State
                          </label>
                          <input
                            type="text"
                            value={ingestForm.state}
                            onChange={(e) =>
                              setIngestForm({
                                ...ingestForm,
                                state: e.target.value,
                              })
                            }
                            placeholder="e.g., CA"
                            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Institution Type
                          </label>
                          <input
                            type="text"
                            value={ingestForm.institutionType}
                            onChange={(e) =>
                              setIngestForm({
                                ...ingestForm,
                                institutionType: e.target.value,
                              })
                            }
                            placeholder="e.g., Bank, CU"
                            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Asset Size ($)
                          </label>
                          <input
                            type="number"
                            value={ingestForm.assetSize}
                            onChange={(e) =>
                              setIngestForm({
                                ...ingestForm,
                                assetSize: e.target.value,
                              })
                            }
                            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mt-6 flex justify-end gap-3">
                      <button
                        onClick={() => setShowIngest(false)}
                        className="rounded-lg border px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleIngest}
                        disabled={
                          !ingestForm.institutionName ||
                          !ingestForm.agency ||
                          ingestMutation.isPending
                        }
                        className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
                      >
                        {ingestMutation.isPending ? 'Ingesting...' : 'Ingest Action'}
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* View Detail Modal */}
        <Transition appear show={!!viewId} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-50"
            onClose={() => setViewId(null)}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl">
                    <div className="flex items-start justify-between">
                      <Dialog.Title className="text-lg font-semibold text-gray-900">
                        Enforcement Action Details
                      </Dialog.Title>
                      <button
                        onClick={() => setViewId(null)}
                        className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                    {viewAction ? (
                      <div className="mt-4 space-y-4">
                        <div>
                          <h4 className="text-base font-semibold text-gray-900">
                            {viewAction.institutionName}
                          </h4>
                          <div className="mt-2 flex items-center gap-2">
                            <span
                              className={clsx(
                                'inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium',
                                (
                                  ACTION_TYPE_BADGE[viewAction.actionType] ||
                                  ACTION_TYPE_BADGE.OTHER_ACTION
                                ).className
                              )}
                            >
                              {(
                                ACTION_TYPE_BADGE[viewAction.actionType] ||
                                ACTION_TYPE_BADGE.OTHER_ACTION
                              ).label}
                            </span>
                            {viewAction.exposureLevel ? (
                              <span
                                className={clsx(
                                  'inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium',
                                  (
                                    EXPOSURE_BADGE[viewAction.exposureLevel] ||
                                    EXPOSURE_BADGE.LOW
                                  ).className
                                )}
                              >
                                Exposure:{' '}
                                {(
                                  EXPOSURE_BADGE[viewAction.exposureLevel] ||
                                  EXPOSURE_BADGE.LOW
                                ).label}
                              </span>
                            ) : (
                              <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-500">
                                Not Analyzed
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="font-medium text-gray-500">Agency</p>
                            <p className="text-gray-900">{viewAction.agency}</p>
                          </div>
                          <div>
                            <p className="font-medium text-gray-500">Action Date</p>
                            <p className="text-gray-900">
                              {viewAction.actionDate
                                ? new Date(
                                    viewAction.actionDate
                                  ).toLocaleDateString()
                                : '--'}
                            </p>
                          </div>
                          <div>
                            <p className="font-medium text-gray-500">Penalty</p>
                            <p className="text-gray-900">
                              {formatPenalty(viewAction.penaltyAmount)}
                            </p>
                          </div>
                          {viewAction.state && (
                            <div>
                              <p className="font-medium text-gray-500">State</p>
                              <p className="text-gray-900">{viewAction.state}</p>
                            </div>
                          )}
                          {viewAction.institutionType && (
                            <div>
                              <p className="font-medium text-gray-500">
                                Institution Type
                              </p>
                              <p className="text-gray-900">
                                {viewAction.institutionType}
                              </p>
                            </div>
                          )}
                          {viewAction.assetSize && (
                            <div>
                              <p className="font-medium text-gray-500">Asset Size</p>
                              <p className="text-gray-900">
                                {formatPenalty(viewAction.assetSize)}
                              </p>
                            </div>
                          )}
                          {viewAction.sourceUrl && (
                            <div>
                              <p className="font-medium text-gray-500">Source URL</p>
                              <a
                                href={viewAction.sourceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                              >
                                View Source
                              </a>
                            </div>
                          )}
                        </div>
                        {viewAction.description && (
                          <div>
                            <p className="text-sm font-medium text-gray-500">
                              Description
                            </p>
                            <p className="mt-1 whitespace-pre-wrap text-sm text-gray-900">
                              {viewAction.description}
                            </p>
                          </div>
                        )}
                        {viewAction.violationTypes &&
                          viewAction.violationTypes.length > 0 && (
                            <div>
                              <p className="text-sm font-medium text-gray-500">
                                Violation Types
                              </p>
                              <div className="mt-1 flex flex-wrap gap-1">
                                {viewAction.violationTypes.map(
                                  (vt: string, i: number) => (
                                    <span
                                      key={i}
                                      className="inline-flex rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700"
                                    >
                                      {vt}
                                    </span>
                                  )
                                )}
                              </div>
                            </div>
                          )}
                        {viewAction.complianceAreas &&
                          viewAction.complianceAreas.length > 0 && (
                            <div>
                              <p className="text-sm font-medium text-gray-500">
                                Compliance Areas
                              </p>
                              <div className="mt-1 flex flex-wrap gap-1">
                                {viewAction.complianceAreas.map(
                                  (area: string, i: number) => (
                                    <span
                                      key={i}
                                      className="inline-flex rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700"
                                    >
                                      {area}
                                    </span>
                                  )
                                )}
                              </div>
                            </div>
                          )}

                        {/* Exposure Analysis Results */}
                        {viewAction.exposureAnalysis && (
                          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                            <h4 className="text-sm font-semibold text-gray-900">
                              Exposure Analysis
                            </h4>
                            <div className="mt-3 space-y-3">
                              <div className="flex items-center gap-4 text-sm">
                                <div>
                                  <span className="font-medium text-gray-500">
                                    Level:{' '}
                                  </span>
                                  <span
                                    className={clsx(
                                      'inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium',
                                      (
                                        EXPOSURE_BADGE[
                                          viewAction.exposureAnalysis.exposureLevel
                                        ] || EXPOSURE_BADGE.LOW
                                      ).className
                                    )}
                                  >
                                    {viewAction.exposureAnalysis.exposureLevel}
                                  </span>
                                </div>
                                {viewAction.exposureAnalysis.score != null && (
                                  <div>
                                    <span className="font-medium text-gray-500">
                                      Score:{' '}
                                    </span>
                                    <span className="text-gray-900">
                                      {viewAction.exposureAnalysis.score}
                                    </span>
                                  </div>
                                )}
                              </div>
                              {viewAction.exposureAnalysis.factors &&
                                viewAction.exposureAnalysis.factors.length > 0 && (
                                  <div>
                                    <p className="text-sm font-medium text-gray-500">
                                      Factors
                                    </p>
                                    <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-gray-900">
                                      {viewAction.exposureAnalysis.factors.map(
                                        (f: string, i: number) => (
                                          <li key={i}>{f}</li>
                                        )
                                      )}
                                    </ul>
                                  </div>
                                )}
                              {viewAction.exposureAnalysis.recommendations &&
                                viewAction.exposureAnalysis.recommendations.length >
                                  0 && (
                                  <div>
                                    <p className="text-sm font-medium text-gray-500">
                                      Recommendations
                                    </p>
                                    <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-gray-900">
                                      {viewAction.exposureAnalysis.recommendations.map(
                                        (r: string, i: number) => (
                                          <li key={i}>{r}</li>
                                        )
                                      )}
                                    </ul>
                                  </div>
                                )}
                            </div>
                          </div>
                        )}

                        {/* Analyze button in detail view */}
                        <div className="flex justify-end">
                          <button
                            onClick={() => handleAnalyze(viewAction.id)}
                            disabled={
                              analyzeMutation.isPending &&
                              analyzeId === viewAction.id
                            }
                            className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
                          >
                            <MagnifyingGlassIcon className="h-4 w-4" />
                            {analyzeMutation.isPending &&
                            analyzeId === viewAction.id
                              ? 'Analyzing...'
                              : 'Run Exposure Analysis'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center py-12">
                        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
                      </div>
                    )}
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div>
    </FeatureGate>
  );
}
